import React from 'react';
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import PrivateRoute from '../../components/shared/PrivateRoute';
import { PERMISSIONS } from '../../helpers/api/auth/permissions';
import DashboardLayout from '../../layouts/DashoardLayout';
import { Box, Paper, SegmentedFilter } from '../../ui/core';
import { flushPageSx, surfaceCardSx } from '../../ui/styles';
import Isochrones from './Isochrones';
import PlaceBoundaries from './PlaceBoundaries';
import LaneMap from './LaneMap';
import LanePolylineEditor from './LanePolylineEditor';

// The nav rail's `sublinks` field is dead — every live page uses the `flush`
// layout, which only reads `activeBase` and ignores sublinks entirely. So the
// Location views need their own in-page switcher instead.
const LOCATION_VIEWS = [
  { key: 'isochrones', label: 'Isochrones', path: '/location/isochrones' },
  { key: 'lane-map', label: 'Lane Map', path: '/location/lane-map' },
  { key: 'place-boundaries', label: 'Place Boundaries', path: '/location/place-boundaries' },
];

const LocationViewSwitcher = () => {
  const history = useHistory();
  const { search } = useLocation();
  // `/location/lanes/:id` belongs to the Lane Map view even though it no
  // longer sits under that path. Matching only `/location/lane-map` left the
  // switcher highlighting Isochrones while an operator was looking at a lane.
  const isLaneMapRoute = !!useRouteMatch({
    path: ['/location/lane-map', '/location/lanes'],
  });
  const isPlaceBoundaryRoute = !!useRouteMatch({
    path: '/location/place-boundaries',
  });
  // Isochrones is the fallback, so every new view needs a case here or it
  // renders with the wrong segment highlighted.
  let activeKey = 'isochrones';
  if (isLaneMapRoute) activeKey = 'lane-map';
  if (isPlaceBoundaryRoute) activeKey = 'place-boundaries';

  const onChange = (key) => {
    const view = LOCATION_VIEWS.find((v) => v.key === key);
    if (!view) return;

    // Carry `cityId` back to the lane map. Its lanes query is
    // `enabled: !!cityId`, so switching to it from a lane page without the
    // parameter lands on a map that fetches nothing — the lane page's own
    // Back button preserves it, and the switcher sitting right above it must
    // not quietly do the opposite. Only for the lane map: Isochrones does not
    // read it.
    const cityId = new URLSearchParams(search).get('cityId');
    const suffix =
      view.key === 'lane-map' && cityId
        ? `?cityId=${encodeURIComponent(cityId)}`
        : '';

    history.push(`${view.path}${suffix}`);
  };

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Box sx={{ width: 280 }}>
        <SegmentedFilter
          value={activeKey}
          onChange={onChange}
          segments={LOCATION_VIEWS.map(({ key, label }) => ({ key, label }))}
        />
      </Box>
    </Box>
  );
};

const LocationShell = () => (
  <Box sx={flushPageSx}>
    <Paper elevation={0} sx={{ ...surfaceCardSx, flex: 1, minWidth: 0 }}>
      <LocationViewSwitcher />
      {/* Both views set height:'100%' on their own root, which only resolves
          correctly against a sized flex item — flex:1 + minHeight:0 gives them
          the remaining space below the switcher instead of the full Paper
          height (which would overflow and get clipped by surfaceCardSx's
          overflow:hidden). */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Switch>
          <PrivateRoute exact path="/location/isochrones" component={Isochrones} />
          {/* One lane's own page: its geometry, its edit history, and the
              polyline editor. Not `/lane-map/:id/edit` any more — that path
              said the page was an editing MODE of the map, and the editor's
              select stage was the only place a lane's history could be seen.
              An operator who saved was redirected away from the one page that
              could show them what they had just done.

              Still behind EDIT_LANE_POLYLINE: the only link to it is the lane
              map's Edit button, which is already gated on the same
              permission, so this opens no new access. If read-only viewing of
              a lane's history is ever wanted, the gate moves from the route
              onto the editing controls. */}
          <PrivateRoute
            exact
            path="/location/lanes/:laneId"
            permission={PERMISSIONS.EDIT_LANE_POLYLINE}
            component={LanePolylineEditor}
          />
          <PrivateRoute exact path="/location/lane-map" component={LaneMap} />
          <PrivateRoute
            exact
            path="/location/place-boundaries"
            component={PlaceBoundaries}
          />
        </Switch>
      </Box>
    </Paper>
  </Box>
);

const Location = () => (
  <DashboardLayout flush title="Location">
    <LocationShell />
  </DashboardLayout>
);

export default Location;
